import List from 'shared/layout/list';
import useProjection from 'shared/migration-hooks/use-projection';
import {
  OrderOriginEnum,
  OrderProps,
  OrderStatus,
  OrderTypeEnum,
} from '../types';
import toBrl from 'shared/util/to-brl';
import {MdCurrencyExchange} from 'react-icons/md';
import {GiPayMoney} from 'react-icons/gi';
import dateFnsService from 'shared/services/date-fns-service';
import useAccessControls from 'modules/access-controls/atoms/use-access-controls';
import StatusButton from 'shared/components/status-button';
import getOrderStatusLabel from '../util/get-order-status-label';
import {orderStatusColors} from './orders-by-status/components/order-status-list';
import {Operators} from 'shared/migration-components/filter-list/types';
import {useEffect} from 'react';
import {PiCashRegister} from 'react-icons/pi';
import {BsTicketPerforated} from 'react-icons/bs';

const Orders: React.FC = () => {
  const projection = useProjection<OrderProps>('orders', [
    'number',
    'companyName',
    'cityName',
    'userName',
    'address.street',
    'address.neighborhood',
    'deliveryManName',
    'deliveryPrice',
    'deliveryManFee',
    'total',
    'paidTotal',
    'payment.name',
    'payment.change',
    'currentStatusName',
    'retainedEarnings',
    'changeSent',
    'consideredTotal',
  ]);

  useEffect(() => {
    projection.filter.onChange({
      ...projection.filter.filters,
      currentStatusName: {operator: Operators.Equals, value: 'finished'},
    });
  }, []);

  const {cityId, companyId} = useAccessControls();

  return (
    <List<OrderProps>
      titleBar={{title: 'Pedidos'}}
      dateFilter={{startDateField: 'createdAt'}}
      analyticsCards={[
        {
          field: 'rawTotal',
          label: 'Total vendido',
          icon: <MdCurrencyExchange />,
          customValue: toBrl,
        },
        {
          field: 'consideredTotal',
          label: 'Total considerado',
          icon: <PiCashRegister />,
          customValue: toBrl,
          hidden: !!companyId,
        },
        {
          field: 'totalDeliveryPrice',
          label: 'Entregas',
          icon: <GiPayMoney />,
          customValue: toBrl,
          hidden: !!companyId,
        },
        {
          field: 'companyAmount',
          label: 'Repasse empresa',
          icon: <GiPayMoney />,
          customValue: toBrl,
          hidden: !!companyId,
        },
        {
          field: (data: OrderProps) =>
            data.companyProductsDiscount + data.companyCouponDiscount,
          label: 'Investimentos empresa',
          icon: <BsTicketPerforated />,
          customValue: toBrl,
        },
        {
          field: 'companyFreeDeliveryPrice',
          label: 'Entrega grátis empresa',
          icon: <GiPayMoney />,
          customValue: toBrl,
        },
        {
          field: 'retainedEarningsAmount',
          label: 'Dinheiro com a empresa',
          icon: <GiPayMoney />,
          customValue: toBrl,
        },
        {
          field: 'companyOccurrences',
          label: 'Ocorrências empresa',
          icon: <GiPayMoney />,
          customValue: toBrl,
          hidden: !!companyId,
        },
        {
          field: 'cityRawAmount',
          label: 'Comissão cidade (Bruta)',
          icon: <GiPayMoney />,
          customValue: toBrl,
          hidden: !!companyId,
        },
        {
          field: (data: OrderProps) =>
            data.cityProductsDiscount +
            data.cityCouponDiscount +
            data.cityFreeDeliveryPrice +
            data.balanceAmount,
          label: 'Investimentos cidade',
          icon: <BsTicketPerforated />,
          customValue: toBrl,
          hidden: !!companyId,
        },
      ]}
      table={{
        url: '/orders',
        projection: {
          ...projection,
          options: [
            'number',
            {value: 'companyName', hidden: !!companyId},
            {value: 'cityName', hidden: !!cityId},
            'userName',
            'address.street',
            'address.neighborhood',
            'deliveryManName',
            'payment.name',
            {
              value: 'retainedEarnings',
              column: (row) => (
                <StatusButton
                  variant={row.retainedEarnings ? 'success' : 'error'}
                  text={row.retainedEarnings ? 'Sim' : 'Não'}
                />
              ),
              filter: {
                type: 'select',
                values: [
                  {
                    label: 'Sim',
                    value: true,
                  },
                  {
                    label: 'Não',
                    value: false,
                  },
                ],
              },
            },
            {
              value: 'origin',
              column: (row) => (
                <StatusButton
                  variant={
                    row.origin === OrderOriginEnum.system
                      ? 'warn'
                      : row.origin === OrderOriginEnum.organic
                      ? 'success'
                      : 'info'
                  }
                  text={
                    row.origin === OrderOriginEnum.system
                      ? 'Lançamento'
                      : row.origin === OrderOriginEnum.organic
                      ? 'Orgânico'
                      : 'Convidado'
                  }
                />
              ),
              filter: {
                type: 'select',
                values: [
                  {
                    label: 'Lançamento',
                    value: OrderOriginEnum.system,
                  },
                  {
                    label: 'Orgânico',
                    value: OrderOriginEnum.organic,
                  },
                  {
                    label: 'Convidado',
                    value: OrderOriginEnum.guest,
                  },
                ],
              },
            },
            {
              value: 'changeSent',
              column: (row) => (
                <StatusButton
                  variant={row.changeSent ? 'success' : 'error'}
                  text={row.changeSent ? 'Sim' : 'Não'}
                />
              ),
              filter: {
                type: 'select',
                values: [
                  {
                    label: 'Sim',
                    value: true,
                  },
                  {
                    label: 'Não',
                    value: false,
                  },
                ],
              },
            },
            {
              value: 'type',
              column: (row) => (
                <StatusButton
                  variant={
                    row.type === OrderTypeEnum.delivery ? 'success' : 'warn'
                  }
                  text={
                    row.type === OrderTypeEnum.delivery ? 'Entrega' : 'Retirada'
                  }
                />
              ),
              filter: {
                type: 'select',
                values: [
                  {
                    label: 'Entrega',
                    value: OrderTypeEnum.delivery,
                  },
                  {
                    label: 'Retirada',
                    value: OrderTypeEnum.pickup,
                  },
                ],
              },
            },
            {
              value: 'createdAt',
              column: (row) => dateFnsService.dateTimeFormat(row.createdAt),
              hideFilter: true,
            },
            {value: 'deliveryPrice', column: (row) => toBrl(row.deliveryPrice)},
            {
              value: 'deliveryManFee',
              column: (row) => toBrl(row.deliveryManFee),
            },
            {
              value: 'total',
              column: (row) => toBrl(row.total),
            },
            {
              value: 'paidTotal',
              column: (row) => toBrl(row.paidTotal),
              filter: {valueAsNumber: true},
            },
            {
              value: 'consideredTotal',
              column: (row) => toBrl(row.consideredTotal),
            },
            {
              value: 'payment.change',
              column: (row) => row.payment.change && toBrl(row.payment.change),
            },
            {
              value: 'currentStatusName',
              column: (row) => (
                <StatusButton
                  text={getOrderStatusLabel(row.currentStatusName)}
                  variant={'success'}
                  color={orderStatusColors[row.currentStatusName]}
                />
              ),
              filter: {
                type: 'select',
                values: [
                  {
                    label: getOrderStatusLabel(OrderStatus.awaiting_payment),
                    value: OrderStatus.awaiting_payment,
                  },
                  {
                    label: getOrderStatusLabel(OrderStatus.opened),
                    value: OrderStatus.opened,
                  },
                  {
                    label: getOrderStatusLabel(OrderStatus.preparing),
                    value: OrderStatus.preparing,
                  },
                  {
                    label: getOrderStatusLabel(OrderStatus.ready_to_pickup),
                    value: OrderStatus.ready_to_pickup,
                  },
                  {
                    label: getOrderStatusLabel(OrderStatus.ready_to_delivery),
                    value: OrderStatus.ready_to_delivery,
                  },
                  {
                    label: getOrderStatusLabel(OrderStatus.delivering),
                    value: OrderStatus.delivering,
                  },
                  {
                    label: getOrderStatusLabel(OrderStatus.arrival),
                    value: OrderStatus.arrival,
                  },
                  {
                    label: getOrderStatusLabel(OrderStatus.finished),
                    value: OrderStatus.finished,
                  },
                  {
                    label: getOrderStatusLabel(OrderStatus.canceled),
                    value: OrderStatus.canceled,
                  },
                ],
              },
            },
          ],
        },
        translatedPropsKey: 'OrderProps',
      }}
    />
  );
};

export default Orders;
